/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import _toNumber from "lodash/toNumber";
import _slice from "lodash/slice";
import Vehicle from "./Vehicle/Vehicle";
import _cloneDeep from "lodash/cloneDeep";
import { Box, MenuItem } from "@mui/material";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import { requiredValidator } from "../../../services/validationRules";
import Typography from "@mui/material/Typography";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import wizardStyle from "../wizardStyle";

type VehiclesProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function Vehicles({ submitProps, product }: VehiclesProps) {
  const { values, handleChange, handleSubmit } = useForm();
  useEffect(() => {
    const numberOfVehicles = values.numberOfVehicles ? _toNumber(values.numberOfVehicles) : 0;
    if (values.vehicles.length === numberOfVehicles) {
      return;
    }
    const emptyVehicle = {
      make: "",
      manufactured: "",
      purchased: "",
      ownership: "",
      mileage: "",
      value: "",
    };
    let vehicles = _cloneDeep(values.vehicles);
    for (let i = vehicles.length; i < Math.min(numberOfVehicles, 5); i++) {
      vehicles.push(_cloneDeep(emptyVehicle));
    }
    vehicles = _slice(vehicles, 0, numberOfVehicles);
    handleChange({
      target: {
        type: "other",
        name: "vehicles",
        value: vehicles,
      },
    });
  }, [values.vehicles, values.numberOfVehicles]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vermögen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasVehicles"}
                question={"Haben Sie ein Kraftfahrzeug (Auto, Motorrad, etc.)?"}
                modalContent={<p>Auch wenn Sie ein Fahrzeug besitzen, kann Ihnen Verfahrenskostenhilfe gewährt werden. Eine Angabe ist daher erforderlich.</p>}
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasVehicles === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie viele Kraftfahrzeug haben Sie?"
                  name="numberOfVehicles"
                  validators={[requiredValidator("Bitte geben Sie an wie viele Kraftfahrzeuge Sie haben")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.vehicles?.map((vehicle: any, index: number) => (
                  <Vehicle key={index} index={index} path={`vehicles[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
